import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PlaylistInput,
  PlaylistUpdateInput,
  QuotaStatus,
  VideoCategory,
  VideoMetadataInput,
  VideoUpdateInput,
  VideoUploadInitInput,
  PublicCaption,
} from "@vedaunion/shared";
import { apiFetch, apiRequest, ApiError } from "./api";

/**
 * Client-side hooks for the `/api/youtube/*` surface.
 *
 * Read endpoints all hit the DB-cached server side (sync runs on cron + admin
 * "refresh now" — never per-request). Hook caches are loose because the
 * underlying data only changes every few hours.
 */

// =============================================================================
// Public read — featured (landing strip)
// =============================================================================

export interface YoutubeVideo {
  id: string;
  title: string;
  thumbnailUrl: string;
  publishedAt: string;
}

export interface YoutubePlaylist {
  id: string;
  title: string;
  thumbnailUrl: string;
  itemCount: number;
}

export interface YoutubeFeatured {
  channelId: string | null;
  channelUrl: string;
  videos: YoutubeVideo[];
  playlists: YoutubePlaylist[];
  unconfigured: boolean;
}

export function useYoutubeFeatured() {
  return useQuery<YoutubeFeatured>({
    queryKey: ["youtube", "featured"] as const,
    queryFn: ({ signal }) =>
      apiFetch<YoutubeFeatured>("/api/youtube/featured", { signal }),
    staleTime: 25 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Public read — full library (paginated videos)
// =============================================================================

export interface YoutubeLibraryVideo {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  publishedAt: string | null;
  durationSec: number | null;
  viewCount: number | null;
}

export interface YoutubeLibraryPage {
  videos: YoutubeLibraryVideo[];
  total: number;
  limit: number;
  offset: number;
  sort: VideoSort;
}

export type VideoSort =
  | "newest"
  | "oldest"
  | "longest"
  | "shortest"
  | "popular"
  | "alpha";

export const VIDEO_SORT_LABELS: Record<VideoSort, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  longest: "Longest",
  shortest: "Shortest",
  popular: "Most viewed",
  alpha: "A → Z",
};

export function useYoutubeLibrary(opts: {
  limit?: number;
  offset?: number;
  q?: string;
  sort?: VideoSort;
}) {
  const { limit = 24, offset = 0, q = "", sort = "newest" } = opts;
  return useQuery<YoutubeLibraryPage>({
    queryKey: ["youtube", "library", limit, offset, q, sort] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        sort,
      });
      if (q) params.set("q", q);
      return apiFetch<YoutubeLibraryPage>(
        `/api/youtube/videos?${params.toString()}`,
        { signal },
      );
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Public read — playlists list
// =============================================================================

export interface YoutubePlaylistSummary {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  itemCount: number;
  updatedAt: string | null;
}

export interface YoutubePlaylistListPage {
  playlists: YoutubePlaylistSummary[];
  total: number;
  limit: number;
  offset: number;
}

export function useYoutubePlaylists(opts: { limit?: number; offset?: number } = {}) {
  const { limit = 24, offset = 0 } = opts;
  return useQuery<YoutubePlaylistListPage>({
    queryKey: ["youtube", "playlists-list", limit, offset] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      return apiFetch<YoutubePlaylistListPage>(
        `/api/youtube/playlists?${params.toString()}`,
        { signal },
      );
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Public read — single playlist with items
// =============================================================================

export interface YoutubePlaylistDetail {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  itemCount: number;
  updatedAt: string | null;
}

export interface YoutubePlaylistItem {
  id: string;
  videoId: string;
  position: number;
  title: string;
  thumbnailUrl: string;
  durationSec: number | null;
  publishedAt: string | null;
}

export interface YoutubePlaylistDetailPayload {
  playlist: YoutubePlaylistDetail;
  items: YoutubePlaylistItem[];
}

export function useYoutubePlaylist(playlistId: string | undefined) {
  return useQuery<YoutubePlaylistDetailPayload>({
    queryKey: ["youtube", "playlist-detail", playlistId] as const,
    enabled: !!playlistId,
    queryFn: ({ signal }) =>
      apiFetch<YoutubePlaylistDetailPayload>(
        `/api/youtube/playlists/${playlistId}`,
        { signal },
      ),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Admin — sync state + manual trigger
// =============================================================================

export interface YoutubeSyncStatus {
  lastFullSyncStartedAt: string | null;
  lastFullSyncFinishedAt: string | null;
  lastFullSyncOk: boolean | null;
  lastError: string | null;
  currentlySyncing: boolean;
  channelId: string | null;
  videosCount: number;
  playlistsCount: number;
  nextRetryAt: string | null;
  quotaExhausted: boolean;
}

export function useYoutubeSyncStatus() {
  return useQuery<YoutubeSyncStatus>({
    queryKey: ["youtube", "sync-status"] as const,
    queryFn: ({ signal }) =>
      apiFetch<YoutubeSyncStatus>("/api/youtube/sync/status", { signal }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface YoutubeSyncResult {
  ok: boolean;
  skipped?: "locked" | "unconfigured";
  durationMs?: number;
  videosCount?: number;
  playlistsCount?: number;
  error?: string;
}

export function useYoutubeRunSync() {
  const qc = useQueryClient();
  return useMutation<YoutubeSyncResult, Error, { force?: boolean } | void>({
    mutationFn: async (opts) => {
      const force = opts && "force" in opts && opts.force ? "?force=1" : "";
      return apiFetch<YoutubeSyncResult>(`/api/youtube/sync${force}`, {
        method: "POST",
        body: {},
      });
    },
    onSettled: () => {
      // Sync may have changed everything we know — invalidate the lot.
      qc.invalidateQueries({ queryKey: ["youtube", "sync-status"] });
      qc.invalidateQueries({ queryKey: ["youtube", "featured"] });
      qc.invalidateQueries({ queryKey: ["youtube", "library"] });
      qc.invalidateQueries({ queryKey: ["youtube", "playlists-list"] });
      qc.invalidateQueries({ queryKey: ["youtube", "admin-videos"] });
      qc.invalidateQueries({ queryKey: ["youtube", "admin-playlists"] });
      qc.invalidateQueries({ queryKey: ["youtube", "admin-audit"] });
    },
  });
}

// =============================================================================
// Admin — OAuth status (Phase 2 placeholder)
// =============================================================================

export interface YoutubeOauthStatus {
  configured: boolean;
  connected: boolean;
  channelTitle: string | null;
  connectedAt: string | null;
  refreshExpiresAt: string | null;
}

export function useYoutubeOauthStatus() {
  return useQuery<YoutubeOauthStatus>({
    queryKey: ["youtube", "oauth-status"] as const,
    queryFn: ({ signal }) =>
      apiFetch<YoutubeOauthStatus>("/api/youtube/oauth/status", { signal }),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface OauthStartResponse {
  authorizeUrl: string;
}

/**
 * Kick off the OAuth flow. The server creates a session-bound state nonce
 * and returns the Google authorize URL — the caller redirects the browser
 * to it. After the user consents, Google bounces back to
 * /api/youtube/oauth/callback (handled server-side), which redirects to
 * /dashboard/youtube?oauth=connected.
 */
export function useYoutubeOauthStart() {
  return useMutation<OauthStartResponse, Error, void>({
    mutationFn: () =>
      apiFetch<OauthStartResponse>("/api/youtube/oauth/start", {
        method: "POST",
        body: {},
      }),
    onSuccess: (data) => {
      // Hard navigation — Google's authorize URL needs a top-level redirect
      // (it can't render in an iframe).
      window.location.assign(data.authorizeUrl);
    },
  });
}

export function useYoutubeOauthDisconnect() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, void>({
    mutationFn: () =>
      apiFetch<{ ok: true }>("/api/youtube/oauth/token", {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["youtube", "oauth-status"] });
      qc.invalidateQueries({ queryKey: ["youtube", "admin-audit"] });
    },
  });
}

// =============================================================================
// Admin — videos table
// =============================================================================

export interface AdminVideo {
  id: string;
  title: string;
  thumbnailUrl: string;
  publishedAt: string | null;
  durationSec: number | null;
  privacyStatus: string | null;
  viewCount: number | null;
  likeCount: number | null;
  syncedAt: string | null;
}

export interface AdminVideosPage {
  videos: AdminVideo[];
  total: number;
  limit: number;
  offset: number;
}

export function useAdminYoutubeVideos(opts: {
  limit?: number;
  offset?: number;
  q?: string;
  privacy?: string;
}) {
  const { limit = 50, offset = 0, q = "", privacy = "" } = opts;
  return useQuery<AdminVideosPage>({
    queryKey: ["youtube", "admin-videos", limit, offset, q, privacy] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (q) params.set("q", q);
      if (privacy) params.set("privacy", privacy);
      return apiFetch<AdminVideosPage>(
        `/api/youtube/admin/videos?${params.toString()}`,
        { signal },
      );
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Admin — playlists list
// =============================================================================

export interface AdminPlaylist {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  itemCount: number;
  privacyStatus: string | null;
  updatedAt: string | null;
  syncedAt: string | null;
}

export interface AdminPlaylistsPage {
  playlists: AdminPlaylist[];
  total: number;
  limit: number;
  offset: number;
}

export function useAdminYoutubePlaylists(opts: {
  limit?: number;
  offset?: number;
  q?: string;
} = {}) {
  const { limit = 100, offset = 0, q = "" } = opts;
  return useQuery<AdminPlaylistsPage>({
    queryKey: ["youtube", "admin-playlists", limit, offset, q] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (q) params.set("q", q);
      return apiFetch<AdminPlaylistsPage>(
        `/api/youtube/admin/playlists?${params.toString()}`,
        { signal },
      );
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// Admin — audit log
// =============================================================================

export interface AuditEntry {
  id: number;
  userId: number | null;
  userName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payloadJson: string | null;
  ok: boolean;
  errorMessage: string | null;
  createdAt: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export function useYoutubeAudit(opts: {
  limit?: number;
  offset?: number;
  action?: string;
} = {}) {
  const { limit = 50, offset = 0, action = "" } = opts;
  return useQuery<AuditPage>({
    queryKey: ["youtube", "admin-audit", limit, offset, action] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (action) params.set("action", action);
      return apiFetch<AuditPage>(
        `/api/youtube/admin/audit?${params.toString()}`,
        { signal },
      );
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// URL helpers
// =============================================================================

/**
 * Embed-iframe URLs.
 *
 * History:
 *   We used to point at `youtube-nocookie.com` with `autoplay=1`. That caused
 *   sporadic Error 153 ("Video player configuration error") on the embedded
 *   player — a configuration mismatch the nocookie domain seems stricter
 *   about. Switching back to www.youtube.com fixes it; the nocookie
 *   behaviour for tracking only kicks in *after* a user interacts with the
 *   iframe, so for landing-page lite-embeds (where the user has to click
 *   before the iframe even mounts) the privacy benefit was minimal anyway.
 *
 *   `autoplay=1` is dropped because: (a) modern browsers block iframe
 *   autoplay unless cross-document gesture rules are satisfied — which the
 *   "click the poster, swap in the iframe" pattern does not always satisfy
 *   — and (b) the user-side gesture of clicking Play *inside* the YouTube
 *   player is one frame away regardless. Keeping the iframe boring means it
 *   can't fail.
 */
export const videoEmbedUrl = (id: string) =>
  `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;

export const playlistEmbedUrl = (id: string) =>
  `https://www.youtube.com/embed/videoseries?list=${id}&rel=0&modestbranding=1`;

export const videoWatchUrl = (id: string) =>
  `https://www.youtube.com/watch?v=${id}`;

export const playlistWatchUrl = (id: string) =>
  `https://www.youtube.com/playlist?list=${id}`;

// =============================================================================
// Display helpers
// =============================================================================

export function formatDuration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatViewCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n < 1_000) return String(n);
  if (n < 10_000) return `${(n / 1_000).toFixed(1)}K`;
  if (n < 1_000_000) return `${Math.round(n / 1_000)}K`;
  if (n < 10_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return `${Math.round(n / 1_000_000)}M`;
}

/**
 * Relative-time label suitable for sync-status indicators.
 * Returns "just now", "12 minutes ago", "2 hours ago", or a formatted date.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 60_000) return "just now";
  const min = Math.round(diffMs / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr} hr ago`;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// =============================================================================
// Phase 3 — write hooks
// =============================================================================

const invalidateYoutubeReads = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["youtube", "featured"] });
  qc.invalidateQueries({ queryKey: ["youtube", "library"] });
  qc.invalidateQueries({ queryKey: ["youtube", "playlists-list"] });
  qc.invalidateQueries({ queryKey: ["youtube", "playlist-detail"] });
  qc.invalidateQueries({ queryKey: ["youtube", "admin-videos"] });
  qc.invalidateQueries({ queryKey: ["youtube", "admin-playlists"] });
  qc.invalidateQueries({ queryKey: ["youtube", "admin-audit"] });
  qc.invalidateQueries({ queryKey: ["youtube", "admin-video-detail"] });
  qc.invalidateQueries({ queryKey: ["youtube", "quota"] });
};

// -----------------------------------------------------------------------------
// Categories (cached read)
// -----------------------------------------------------------------------------

export function useYoutubeCategories() {
  return useQuery<{ categories: VideoCategory[] }>({
    queryKey: ["youtube", "categories"] as const,
    queryFn: ({ signal }) =>
      apiFetch<{ categories: VideoCategory[] }>(
        "/api/youtube/categories",
        { signal },
      ),
    // Categories rarely change; cache for the session.
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// -----------------------------------------------------------------------------
// Quota status (Advanced page)
// -----------------------------------------------------------------------------

export function useYoutubeQuota() {
  return useQuery<QuotaStatus>({
    queryKey: ["youtube", "quota"] as const,
    queryFn: ({ signal }) =>
      apiFetch<QuotaStatus>("/api/youtube/quota/status", { signal }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    // Refetch every 30s while the Advanced page is open. Disabled
    // automatically by React Query when no component subscribes.
    refetchInterval: 30_000,
  });
}

// -----------------------------------------------------------------------------
// Single video detail (EditVideo page)
// -----------------------------------------------------------------------------

export interface AdminVideoDetail {
  id: string;
  title: string;
  description: string;
  tags: string[];
  categoryId: string;
  defaultLanguage: string | null;
  defaultAudioLanguage: string | null;
  privacyStatus: "private" | "unlisted" | "public";
  publishAt: string | null;
  selfDeclaredMadeForKids: boolean;
  containsSyntheticMedia: boolean;
  embeddable: boolean;
  publicStatsViewable: boolean;
  license: "youtube" | "creativeCommon";
  recordingDate: string | null;
  duration: string | null;
  viewCount: number | null;
  likeCount: number | null;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  localizations: Array<{
    language: string;
    title: string;
    description: string;
  }>;
  playlistIds: string[];
}

export function useAdminVideoDetail(videoId: string | undefined) {
  return useQuery<AdminVideoDetail>({
    queryKey: ["youtube", "admin-video-detail", videoId] as const,
    enabled: !!videoId,
    queryFn: ({ signal }) =>
      apiFetch<AdminVideoDetail>(
        `/api/youtube/admin/videos/${videoId}`,
        { signal },
      ),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// -----------------------------------------------------------------------------
// Upload — init + finalize
// -----------------------------------------------------------------------------

/**
 * Init the proxied upload session. Server returns just `{uploadId, expiresAt}` —
 * the YouTube session URL stays server-side. The client then POSTs the file
 * body to `/api/youtube/upload/bytes/:uploadId` (handled by `UploadEngine`,
 * not a hook because XHR + progress events).
 */
export interface UploadInitClientResponse {
  uploadId: string;
  expiresAt: string;
}
export function useUploadInit() {
  return useMutation<UploadInitClientResponse, ApiError, VideoUploadInitInput>({
    mutationFn: (body) =>
      apiFetch<UploadInitClientResponse>("/api/youtube/upload/init", {
        method: "POST",
        body,
      }),
  });
}

/**
 * Invalidates the YouTube read caches after a successful upload. Called by
 * the Upload page once the streaming POST returns a videoId.
 */
export function useInvalidateAfterUpload() {
  const qc = useQueryClient();
  return () => invalidateYoutubeReads(qc);
}

// -----------------------------------------------------------------------------
// Video — update / thumbnail / delete
// -----------------------------------------------------------------------------

export function useUpdateVideo(videoId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, VideoUpdateInput>({
    mutationFn: (body) =>
      apiFetch<{ ok: true }>(`/api/youtube/videos/${videoId}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      invalidateYoutubeReads(qc);
      qc.invalidateQueries({
        queryKey: ["youtube", "admin-video-detail", videoId],
      });
    },
  });
}

/**
 * Thumbnail upload — bypasses apiFetch because the body must be the raw
 * image bytes (not a JSON-encoded string). Posts to a server endpoint that
 * proxies through to YouTube's thumbnails.set.
 */
export function useSetThumbnail(videoId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, Blob>({
    mutationFn: async (file) => {
      const res = await apiRequest(`/api/youtube/videos/${videoId}/thumbnail`, {
        method: "POST",
        headers: { "Content-Type": file.type || "image/jpeg" },
        body: file,
      });
      if (!res.ok) {
        let body: { error?: string } = {};
        try {
          body = await res.json();
        } catch {
          /* */
        }
        throw new ApiError(
          res.status,
          body.error ?? `Thumbnail upload failed (${res.status})`,
          body,
        );
      }
      return (await res.json()) as { ok: true };
    },
    onSuccess: () => {
      invalidateYoutubeReads(qc);
      qc.invalidateQueries({
        queryKey: ["youtube", "admin-video-detail", videoId],
      });
    },
  });
}

export function useDeleteVideo() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (videoId) =>
      apiFetch<{ ok: true }>(`/api/youtube/videos/${videoId}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

// -----------------------------------------------------------------------------
// Playlists — create / update / delete
// -----------------------------------------------------------------------------

export function useCreatePlaylist() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, ApiError, PlaylistInput>({
    mutationFn: (body) =>
      apiFetch<{ id: string }>("/api/youtube/playlists", {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

export function useUpdatePlaylist(playlistId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, PlaylistUpdateInput>({
    mutationFn: (body) =>
      apiFetch<{ ok: true }>(`/api/youtube/playlists/${playlistId}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

export function useDeletePlaylist() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (playlistId) =>
      apiFetch<{ ok: true }>(`/api/youtube/playlists/${playlistId}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

// -----------------------------------------------------------------------------
// Playlist items — add / remove / reorder
// -----------------------------------------------------------------------------

export function useAddPlaylistItem(playlistId: string) {
  const qc = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    { videoId: string; position?: number }
  >({
    mutationFn: (body) =>
      apiFetch<{ id: string }>(
        `/api/youtube/playlists/${playlistId}/items`,
        { method: "POST", body },
      ),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

export function useRemovePlaylistItem() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (itemId) =>
      apiFetch<{ ok: true }>(`/api/youtube/playlist-items/${itemId}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

export function useReorderPlaylistItem() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    ApiError,
    { itemId: string; position: number }
  >({
    mutationFn: ({ itemId, position }) =>
      apiFetch<{ ok: true }>(
        `/api/youtube/playlist-items/${itemId}/position`,
        { method: "PATCH", body: { position } },
      ),
    onSuccess: () => invalidateYoutubeReads(qc),
  });
}

// -----------------------------------------------------------------------------
// Captions
// -----------------------------------------------------------------------------

export function useVideoCaptions(videoId: string | undefined) {
  return useQuery<{ captions: PublicCaption[] }>({
    queryKey: ["youtube", "captions", videoId] as const,
    enabled: !!videoId,
    queryFn: ({ signal }) =>
      apiFetch<{ captions: PublicCaption[] }>(
        `/api/youtube/videos/${videoId}/captions`,
        { signal },
      ),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useUploadCaption(videoId: string) {
  const qc = useQueryClient();
  return useMutation<
    { id: string },
    ApiError,
    { file: Blob; language: string; name: string; isDraft: boolean }
  >({
    mutationFn: async ({ file, language, name, isDraft }) => {
      const meta = btoa(
        unescape(
          encodeURIComponent(JSON.stringify({ language, name, isDraft })),
        ),
      );
      const res = await apiRequest(`/api/youtube/videos/${videoId}/captions`, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "text/plain",
          "X-Caption-Meta": meta,
        },
        body: file,
      });
      if (!res.ok) {
        let body: { error?: string } = {};
        try {
          body = await res.json();
        } catch {
          /* */
        }
        throw new ApiError(
          res.status,
          body.error ?? `Caption upload failed (${res.status})`,
          body,
        );
      }
      return (await res.json()) as { id: string };
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["youtube", "captions", videoId] }),
  });
}

export function useDeleteCaption(videoId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (captionId) =>
      apiFetch<{ ok: true }>(`/api/youtube/captions/${captionId}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["youtube", "captions", videoId] }),
  });
}

// -----------------------------------------------------------------------------
// VideoMetadataInput — re-export for editor pages convenience
// -----------------------------------------------------------------------------

export type { VideoMetadataInput, VideoUpdateInput, PlaylistInput };
